import React from 'react';
import { TextInput, BooleanInput, SaveButton, Toolbar } from 'react-admin';
import { Box, Typography, Divider } from '@material-ui/core';
import { useTranslate } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        width: '100%',
    },
    saveButton: {
        maxWidth: '300px',
    },
    inputGroup: {
        display: 'flex',
        alignItems: 'flex-start',
        '& > *:not(:last-child)': {
            marginRight: theme.spacing(2),
        },
        width: '100%',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            '& > *:not(:last-child)': {
                marginRight: 0,
                marginBottom: theme.spacing(2),
            },
        },
    },
    inputContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        '& > *:not(:last-child)': {
            marginBottom: theme.spacing(2),
        },
    },
    booleanContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        '& > *:not(:last-child)': {
            marginBottom: theme.spacing(1),
        },
    },
    sectionTitle: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
    appTitle: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.main,
    },
    platformTitle: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
}));

interface PlatformSectionProps {
    appType: 'appClient' | 'appLivreur';
    platform: 'android' | 'ios';
    translate: (key: string) => string;
    classes: any;
}

const PlatformSection: React.FC<PlatformSectionProps> = ({
    appType,
    platform,
    translate,
    classes,
}) => (
    <>
        <Typography variant="h6" className={classes.platformTitle}>
            {translate(`resources.settings.platforms.${platform}`)}
        </Typography>
        <Box className={classes.inputGroup}>
            <Box className={classes.inputContainer}>
                <TextInput
                    source={`${appType}.${platform}.storeVersion`}
                    label={translate('resources.settings.fields.storeVersion')}
                    fullWidth
                />
                <TextInput
                    source={`${appType}.${platform}.storeURL`}
                    label={translate('resources.settings.fields.storeURL')}
                    fullWidth
                />
            </Box>
            <Box className={classes.booleanContainer}>
                <BooleanInput
                    source={`${appType}.${platform}.forceUpgrade`}
                    label={translate('resources.settings.fields.forceUpgrade')}
                />
                <BooleanInput
                    source={`${appType}.${platform}.maintenanceMode`}
                    label={translate(
                        'resources.settings.fields.maintenanceMode'
                    )}
                />
            </Box>
        </Box>
    </>
);

interface AppSectionProps {
    appType: 'appClient' | 'appLivreur';
    translate: (key: string) => string;
    classes: any;
}

const AppSection: React.FC<AppSectionProps> = ({
    appType,
    translate,
    classes,
}) => (
    <>
        <Typography variant="h4" className={classes.appTitle}>
            {translate(`resources.settings.apps.${appType}`)}
        </Typography>

        <PlatformSection
            appType={appType}
            platform="ios"
            translate={translate}
            classes={classes}
        />

        <PlatformSection
            appType={appType}
            platform="android"
            translate={translate}
            classes={classes}
        />
    </>
);

const VersionEdit = () => {
    const translate = useTranslate();
    const classes = useStyles();

    return (
        <>
            {/* Client App Section */}
            <AppSection
                appType="appClient"
                translate={translate}
                classes={classes}
            />

            <Divider className={classes.divider} />

            {/* Livreur App Section */}
            <AppSection
                appType="appLivreur"
                translate={translate}
                classes={classes}
            />

        </>
    );
};

export default VersionEdit;
